import React, { useState, useEffect } from 'react';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { useCalculator } from '../context/CalculatorContext';
import WhatIfScenarioConfig from './WhatIfScenarioConfig';
import WhatIfComparisonView from './WhatIfComparisonView';

interface WhatIfScenarioModeProps {
  onBack: () => void;
}

const WhatIfScenarioMode: React.FC<WhatIfScenarioModeProps> = ({ onBack }) => {
  const [activeView, setActiveView] = useState<'config' | 'comparison'>('config');
  const { clearWhatIfScenario, whatIfScenario, whatIfResults } = useCalculator();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (activeView === 'comparison') {
      setActiveView('config');
      // Clear the scenario when going back to config
      clearWhatIfScenario();
      // Scroll to top when going back to config
      window.scrollTo(0, 0);
    } else {
      clearWhatIfScenario();
      onBack();
    }
  };

  const handleNext = () => {
    setActiveView('comparison');
    // Scroll to top when going to comparison view
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-red-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-6">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100/50 rounded-xl transition-all duration-200"
              title="Volver"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Volver</span>
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-red-600 rounded-2xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Certificado de Resiliencia Financiera</h1>
                <p className="text-gray-600">Experimenta con escenarios extremos para evaluar la resiliencia de tu inversión</p>
              </div>
              
              {/* Disclaimer */}
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800">
                  <strong>⚠️ Aviso:</strong> Las proyecciones mostradas son estimaciones y no garantizan rendimientos futuros.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeView === 'config' ? (
          <WhatIfScenarioConfig onNext={handleNext} onBack={handleBack} />
        ) : (
          <WhatIfComparisonView onBack={handleBack} />
        )}
      </div>
    </div>
  );
};

export default WhatIfScenarioMode;